import React, { useState, useEffect } from 'react';
import { Camera, Edit3, Heart, Star, MapPin, Briefcase, GraduationCap, Music, Coffee, Mountain, Book, Camera as CameraIcon, Gamepad2, Palette, Dumbbell, Brain } from 'lucide-react';
import EnhancedButton from './EnhancedButton';
import { InteractiveRating, ProgressRing } from './InteractiveComponents';
import { LoadingSpinner, SkeletonLoader } from './LoadingStates';

// Enhanced Profile Gallery
export const ProfileGallery = ({ photos = [], onPhotoUpload, canEdit = false }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  const defaultPhotos = [
    "https://images.unsplash.com/photo-1627161684458-a62da52b51c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMG1hbGUlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NTQxMjk3NjR8MA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxmZW1hbGUlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NTQxMjk4MjJ8MA&ixlib=rb-4.1.0&q=85",
    "https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg"
  ];

  const displayPhotos = photos.length > 0 ? photos : defaultPhotos;

  const handleImageError = () => {
    setImageLoadError(true);
  };

  const handleImageLoad = () => {
    setImageLoadError(false);
  };

  const handleTouchStart = (e) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    // Prevent page scrolling while swiping on image
    e.preventDefault();
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    
    const distance = touchStartX - touchEndX;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && selectedPhoto < displayPhotos.length - 1) {
      setSelectedPhoto(selectedPhoto + 1);
    }
    if (isRightSwipe && selectedPhoto > 0) {
      setSelectedPhoto(selectedPhoto - 1);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      // Simulate upload
      setTimeout(() => {
        onPhotoUpload && onPhotoUpload(URL.createObjectURL(file));
        setUploading(false);
      }, 2000);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden animate-fadeInUp">
      {/* Main Photo Display */}
      <div 
        className="relative w-full bg-gray-200 overflow-hidden group touch-pan-x aspect-[4/5] sm:aspect-[3/4] md:aspect-[4/5] lg:aspect-[3/4]"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'pan-y' }}
      >
        {uploading ? (
          <div className="h-full flex items-center justify-center">
            <LoadingSpinner text="Uploading photo..." />
          </div>
        ) : imageLoadError ? (
          <div className="h-full flex flex-col items-center justify-center bg-gray-200 text-gray-500">
            <Camera className="w-16 h-16 mb-4" />
            <p className="text-lg font-medium">Demo Profile Image</p>
            <p className="text-sm">Professional headshot placeholder</p>
          </div>
        ) : (
          <img
            src={displayPhotos[selectedPhoto]}
            alt="Profile"
            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105 bg-gray-100"
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        )}
        
        {/* Navigation Arrows */}
        {displayPhotos.length > 1 && (
          <>
            {selectedPhoto > 0 && (
              <button
                onClick={() => setSelectedPhoto(selectedPhoto - 1)}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 backdrop-blur-md text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-black/70"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            {selectedPhoto < displayPhotos.length - 1 && (
              <button
                onClick={() => setSelectedPhoto(selectedPhoto + 1)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 backdrop-blur-md text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-black/70"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </>
        )}
        
        {/* Photo Navigation */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {displayPhotos.map((_, index) => (
            <button
              key={index}
              onClick={() => setSelectedPhoto(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === selectedPhoto 
                  ? 'bg-white scale-125' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>

        {/* Upload Button */}
        {canEdit && (
          <div className="absolute top-4 right-4">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <div className="bg-black/50 backdrop-blur-md text-white p-2 rounded-full hover:bg-black/70 transition-colors">
                <Camera className="w-5 h-5" />
              </div>
            </label>
          </div>
        )}
      </div>

      {/* Photo Thumbnails */}
      <div className="p-4">
        <div className="grid grid-cols-6 gap-2">
          {displayPhotos.map((photo, index) => (
            <button
              key={index}
              onClick={() => setSelectedPhoto(index)}
              className={`aspect-square rounded-lg overflow-hidden border-2 transition-all duration-300 hover-lift ${
                index === selectedPhoto 
                  ? 'border-rose-500 ring-2 ring-rose-500/20' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <img
                src={photo}
                alt={`Photo ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
          
          {/* Add Photo Button */}
          {canEdit && displayPhotos.length < 6 && (
            <label className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-rose-500 hover:bg-rose-50 transition-colors group">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <Camera className="w-6 h-6 text-gray-400 group-hover:text-rose-500" />
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

// Personality Insights Component (DEMO ONLY for Advanced Features page)
export const PersonalityInsights = ({ traits = {}, compatibility = {} }) => {
  const defaultTraits = {
    extroversion: 75,
    agreeableness: 85,
    conscientiousness: 70,
    neuroticism: 30,
    openness: 90
  };

  const personalityData = Object.keys(traits).length > 0 ? traits : defaultTraits;

  const traitDescriptions = {
    extroversion: { label: "Extroversion", desc: "Outgoing and energetic", color: "blue" },
    agreeableness: { label: "Agreeableness", desc: "Friendly and compassionate", color: "green" },
    conscientiousness: { label: "Conscientiousness", desc: "Organized and responsible", color: "purple" },
    neuroticism: { label: "Emotional Stability", desc: "Calm and resilient", color: "rose", inverted: true },
    openness: { label: "Openness", desc: "Creative and curious", color: "orange" }
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 shadow-lg animate-fadeInUp">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
          <Star className="w-6 h-6 text-purple-500" />
          <span>Personality Insights</span>
        </h3>
        <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
          Example Preview
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(personalityData).map(([trait, score], index) => {
          const info = traitDescriptions[trait];
          const displayScore = info?.inverted ? 100 - score : score;
          
          return (
            <div key={trait} className={`animate-slideInLeft stagger-${index + 1}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-semibold text-gray-800">{info?.label}</h4>
                  <p className="text-sm text-gray-600">{info?.desc}</p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-gray-700">{displayScore}%</span>
                </div>
              </div>
              
              <div className="relative">
                <ProgressRing 
                  progress={displayScore} 
                  size={80} 
                  color={info?.color} 
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Compatibility Matches */}
      {Object.keys(compatibility).length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-4">Best Compatibility With</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(compatibility).map(([type, percentage]) => (
              <div
                key={type}
                className="bg-gradient-to-r from-rose-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-medium animate-scaleIn"
              >
                {type}: {percentage}%
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Advanced Interests & Hobbies
export const InterestsMatrix = ({ interests = [], onUpdateInterests, canEdit = false }) => {
  const [selectedCategories, setSelectedCategories] = useState(new Set(interests));

  const interestCategories = {
    "Adventure": {
      icon: <Mountain className="w-5 h-5" />,
      items: ["Hiking", "Rock Climbing", "Skydiving", "Camping", "Surfing", "Skiing"],
      color: "green"
    },
    "Arts & Culture": {
      icon: <Palette className="w-5 h-5" />,
      items: ["Painting", "Photography", "Museums", "Theater", "Dancing", "Writing"],
      color: "purple"
    },
    "Food & Drink": {
      icon: <Coffee className="w-5 h-5" />,
      items: ["Cooking", "Wine Tasting", "Craft Beer", "Fine Dining", "Baking", "Coffee"],
      color: "amber"
    },
    "Fitness": {
      icon: <Dumbbell className="w-5 h-5" />,
      items: ["Gym", "Yoga", "Running", "Cycling", "Swimming", "Martial Arts"],
      color: "red"
    },
    "Entertainment": {
      icon: <Gamepad2 className="w-5 h-5" />,
      items: ["Gaming", "Movies", "TV Shows", "Concerts", "Festivals", "Comedy Shows"],
      color: "blue"
    },
    "Learning": {
      icon: <Book className="w-5 h-5" />,
      items: ["Reading", "Languages", "Podcasts", "Online Courses", "Documentaries", "Science"],
      color: "indigo"
    }
  };

  const toggleInterest = (interest) => {
    if (!canEdit) return;
    
    const newSelected = new Set(selectedCategories);
    if (newSelected.has(interest)) {
      newSelected.delete(interest);
    } else {
      newSelected.add(interest);
    }
    setSelectedCategories(newSelected);
    onUpdateInterests && onUpdateInterests(Array.from(newSelected));
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 animate-fadeInUp">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
          <Heart className="w-6 h-6 text-rose-500" />
          <span>Interests & Hobbies</span>
        </h3>
        {canEdit && (
          <EnhancedButton size="sm" variant="outline" icon={<Edit3 className="w-4 h-4" />}>
            Edit
          </EnhancedButton>
        )}
      </div>

      <div className="space-y-6">
        {Object.entries(interestCategories).map(([category, data], index) => (
          <div key={category} className={`animate-slideInUp stagger-${index + 1}`}>
            <div className="flex items-center space-x-2 mb-3">
              <div className={`p-2 rounded-lg bg-${data.color}-100 text-${data.color}-600`}>
                {data.icon}
              </div>
              <h4 className="font-semibold text-gray-800">{category}</h4>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {data.items.map((interest) => (
                <button
                  key={interest}
                  onClick={() => toggleInterest(interest)}
                  disabled={!canEdit}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover-lift ${
                    selectedCategories.has(interest)
                      ? `bg-gradient-to-r from-${data.color}-500 to-${data.color}-600 text-white animate-pulse`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  {interest}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Interest Stats */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="animate-scaleIn">
            <div className="text-2xl font-bold text-rose-500">{selectedCategories.size}</div>
            <div className="text-sm text-gray-600">Interests</div>
          </div>
          <div className="animate-scaleIn stagger-1">
            <div className="text-2xl font-bold text-purple-500">
              {Math.floor(selectedCategories.size / Object.keys(interestCategories).length * 100)}%
            </div>
            <div className="text-sm text-gray-600">Profile Complete</div>
          </div>
          <div className="animate-scaleIn stagger-2">
            <div className="text-2xl font-bold text-blue-500">
              {Object.keys(interestCategories).filter(cat => 
                interestCategories[cat].items.some(item => selectedCategories.has(item))
              ).length}
            </div>
            <div className="text-sm text-gray-600">Categories</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile Completion Tracker
export const ProfileCompletionTracker = ({ completionData = {} }) => {
  const defaultCompletion = {
    photos: { completed: true, weight: 25, label: "Photos uploaded" },
    bio: { completed: true, weight: 20, label: "Bio written" },
    interests: { completed: false, weight: 20, label: "Interests selected" },
    personality: { completed: true, weight: 15, label: "Personality quiz" },
    preferences: { completed: false, weight: 10, label: "Dating preferences" },
    verification: { completed: false, weight: 10, label: "Profile verified" }
  };

  const completion = Object.keys(completionData).length > 0 ? completionData : defaultCompletion;
  
  const totalScore = Object.values(completion).reduce((sum, item) => 
    sum + (item.completed ? item.weight : 0), 0
  );
  
  const completedCount = Object.values(completion).filter(item => item.completed).length;
  const totalCount = Object.values(completion).length;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 shadow-lg animate-fadeInUp">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-800">Profile Completion</h3>
          <p className="text-gray-600">Complete your profile to get better matches</p>
        </div>
        <div className="text-center">
          <ProgressRing progress={totalScore} size={80} color="blue" />
        </div>
      </div>

      <div className="space-y-3">
        {Object.entries(completion).map(([key, item], index) => (
          <div
            key={key}
            className={`flex items-center justify-between p-3 rounded-lg transition-all duration-300 animate-slideInLeft ${
              item.completed 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
            }`}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                item.completed 
                  ? 'bg-green-500 text-white animate-bounce' 
                  : 'bg-gray-300 text-gray-500'
              }`}>
                {item.completed ? '✓' : index + 1}
              </div>
              <span className={`font-medium ${
                item.completed ? 'text-green-800' : 'text-gray-700'
              }`}>
                {item.label}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-bold text-blue-600">+{item.weight}%</span>
              {!item.completed && (
                <EnhancedButton size="sm" variant="outline">
                  Complete
                </EnhancedButton>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">
            {completedCount} of {totalCount} sections completed
          </span>
          <span className="font-semibold text-blue-600">
            {totalScore}% complete
          </span>
        </div>
      </div>
    </div>
  );
};

export default {
  ProfileGallery,
  PersonalityInsights,
  InterestsMatrix,
  ProfileCompletionTracker
};